import {
  Box,
  TextField,
  Button,
  Typography,
  Link,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Stack,
} from '@mui/material'
import { FC, FormEvent, useState } from 'react'
import { keyframes } from '@emotion/react'
import { useForm } from 'react-hook-form'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined'
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined'
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { useAuth } from '@/hooks/useAuth'
import { RecoveryCodesDisplay } from '@/components/common/RecoveryCodesDisplay'
import { LoginRequest, TotpEnrollResponse } from '@/types/auth'
import { AUTH_PANEL_BG, fieldLabelSx, fieldInputSx, primaryBtnSx } from './authStyles'

const loginSchema = z.object({
  email: z.string().email('Email inválido').min(1, 'Email é obrigatório'),
  senha: z.string().min(6, 'Senha deve ter pelo menos 6 caracteres'),
})

type LoginFormData = z.infer<typeof loginSchema>

const OrigineiLogo: FC<{ size?: number; offsetLeft?: number }> = ({ size = 56, offsetLeft = 0 }) => (
  <img
    src="/logo.png"
    alt="Originei"
    style={{
      height: size,
      width: 'auto',
      display: 'block',
      marginLeft: offsetLeft ? `-${offsetLeft}px` : undefined,
    }}
  />
)

const VALUE_PROPS = [
  {
    icon: <BoltOutlinedIcon sx={{ fontSize: 20 }} />,
    title: 'Pipeline em tempo real',
    desc: 'Acompanhe reservas, etapas e prazos em um único painel.',
  },
  {
    icon: <InsightsOutlinedIcon sx={{ fontSize: 20 }} />,
    title: 'Inteligência aplicada',
    desc: 'Análise documental e busca semântica com IA integrada.',
  },
  {
    icon: <VerifiedUserOutlinedIcon sx={{ fontSize: 20 }} />,
    title: 'Conformidade total',
    desc: 'LGPD, Banco Central e trilha de auditoria em cada operação.',
  },
]

// Animações dos cards de destaque (estilo "hero-visual" fluido).
// Entrada escalonada (fade + slide) + flutuação orgânica contínua + brilho no ícone.
const cardEnter = keyframes`
  from { opacity: 0; transform: translateY(18px); }
  to   { opacity: 1; transform: translateY(0); }
`
const cardFloat = keyframes`
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(-7px); }
`
const iconGlow = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(255, 98, 12,0); }
  50%      { box-shadow: 0 0 18px 0 rgba(255, 98, 12,0.45); }
`

export const LoginPage: FC = () => {
  const navigate = useNavigate()
  const { login, enrollStart, enrollConfirm, isLoading, error } = useAuth()
  const [showPassword, setShowPassword] = useState(false)
  const [remember, setRemember] = useState(true)

  // Fluxo de 2FA:
  //  'senha'          → email+senha
  //  'codigo'         → verificação (usuário já tem 2FA): código TOTP ou de recuperação
  //  'enroll'         → cadastro (usuário sem 2FA): QR + primeiro código
  //  'recovery-codes' → exibe os códigos de recuperação após o cadastro
  const [step, setStep] = useState<'senha' | 'codigo' | 'enroll' | 'recovery-codes'>('senha')
  const [pendingCreds, setPendingCreds] = useState<LoginRequest | null>(null)
  const [codigo, setCodigo] = useState('')
  const [enrollData, setEnrollData] = useState<TotpEnrollResponse | null>(null)
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([])
  // Na etapa 'codigo', alterna entre app autenticador e código de recuperação.
  const [usandoRecovery, setUsandoRecovery] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  })

  const onSubmit = async (data: LoginFormData) => {
    const request: LoginRequest = { email: data.email, senha: data.senha }
    const outcome = await login(request)
    if (outcome.status === 'totp') {
      // Usuário já tem 2FA — pede o código do autenticador.
      setPendingCreds(request)
      setCodigo('')
      setUsandoRecovery(false)
      setStep('codigo')
    } else if (outcome.status === 'enroll') {
      // Usuário ainda não tem 2FA — gera o QR e mostra a tela de cadastro.
      const data2 = await enrollStart(request)
      if (data2) {
        setPendingCreds(request)
        setEnrollData(data2)
        setCodigo('')
        setStep('enroll')
      }
    }
    // 'ok' já navegou; 'error' já exibiu a mensagem via store.
  }

  const onSubmitCodigo = async (e: FormEvent) => {
    e.preventDefault()
    if (!pendingCreds) return
    if (usandoRecovery) {
      if (!codigo.trim()) return
      await login({ ...pendingCreds, recoveryCode: codigo.trim() })
    } else {
      if (codigo.length !== 6) return
      await login({ ...pendingCreds, totp: codigo })
    }
    // 'ok' navega; 'error' exibe a mensagem.
  }

  const onSubmitEnroll = async (e: FormEvent) => {
    e.preventDefault()
    if (!pendingCreds || !enrollData || codigo.length !== 6) return
    const outcome = await enrollConfirm(pendingCreds, enrollData.setupToken, codigo)
    if (outcome.status === 'ok') {
      // Sessão criada; exibe os códigos de recuperação antes de entrar.
      setRecoveryCodes(outcome.recoveryCodes)
      setStep('recovery-codes')
    }
    // 'error' exibe a mensagem.
  }

  const alternarRecovery = () => {
    setUsandoRecovery((v) => !v)
    setCodigo('')
  }

  const voltarParaSenha = () => {
    setStep('senha')
    setPendingCreds(null)
    setEnrollData(null)
    setCodigo('')
    setUsandoRecovery(false)
  }

  // Validação do campo de código: recuperação aceita texto não-vazio; TOTP exige 6 dígitos.
  // (No enroll, usandoRecovery é sempre false → exige 6 dígitos.)
  const codigoValido = usandoRecovery ? codigo.trim().length > 0 : codigo.length === 6

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1.05fr 1fr' },
        backgroundColor: AUTH_PANEL_BG,
      }}
    >
      {/* ─────────────────────────────────────────────────────────
         PAINEL ESQUERDO — Branding / Enterprise marketing
      ───────────────────────────────────────────────────────── */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          position: 'relative',
          flexDirection: 'column',
          justifyContent: 'space-between',
          padding: { md: '48px 56px', lg: '56px 72px' },
          color: '#FFFFFF',
          backgroundColor: '#0A0A0A',
          backgroundImage: `
            radial-gradient(ellipse 80% 60% at 15% 20%, rgba(255, 98, 12,0.22) 0%, transparent 55%),
            radial-gradient(ellipse 60% 50% at 90% 90%, rgba(255,90,26,0.14) 0%, transparent 55%),
            linear-gradient(180deg, #0A0A0A 0%, #111111 100%)
          `,
          overflow: 'hidden',
        }}
      >
        {/* Pattern de grid sutil */}
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)',
            backgroundSize: '48px 48px',
            maskImage: 'radial-gradient(ellipse 90% 70% at 30% 40%, black 30%, transparent 75%)',
            WebkitMaskImage:
              'radial-gradient(ellipse 90% 70% at 30% 40%, black 30%, transparent 75%)',
            pointerEvents: 'none',
          }}
        />

        {/* Topo: logo + tagline alinhados a esquerda. A logo oficial (11/09) não tem
            respiro interno: nada de offset nem margem negativa na legenda. */}
        <Box sx={{ position: 'relative', zIndex: 1, display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          <OrigineiLogo size={150} />
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mt: 1,
              color: 'rgba(255,255,255,0.55)',
              fontSize: '0.75rem',
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              fontWeight: 600,
              textAlign: 'left',
            }}
          >
            Plataforma de Consultores
          </Typography>
        </Box>

        {/* Meio: headline + value props */}
        <Box sx={{ position: 'relative', zIndex: 1, maxWidth: 520 }}>
          <Typography
            sx={{
              fontSize: { md: '2.25rem', lg: '2.75rem' },
              fontWeight: 700,
              lineHeight: 1.1,
              letterSpacing: '-0.03em',
              mb: 2,
            }}
          >
            Do{' '}
            <Box component="span" sx={{ color: '#ff7d37' }}>
              crédito
            </Box>{' '}
            à{' '}
            <Box component="span" sx={{ color: '#ff7d37' }}>
              estratégia
            </Box>
            .
          </Typography>
          <Typography
            sx={{
              color: 'rgba(255,255,255,0.65)',
              fontSize: '1rem',
              lineHeight: 1.6,
              letterSpacing: '-0.01em',
              mb: 5,
              maxWidth: 460,
            }}
          >
            Centralize reservas, documentos, comissões e operações com a inteligência
            que a sua carteira de clientes merece.
          </Typography>

          <Stack spacing={2.5}>
            {VALUE_PROPS.map((item, i) => (
              // Wrapper: entrada escalonada (fade + slide-up, uma vez).
              <Box
                key={item.title}
                sx={{
                  animation: `${cardEnter} 0.6s cubic-bezier(0.22,1,0.36,1) ${0.25 + i * 0.12}s both`,
                  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                }}
              >
                {/* Card: flutuação orgânica contínua, fase distinta por índice. */}
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="flex-start"
                  sx={{
                    animation: `${cardFloat} 6s ease-in-out ${i * 1.1}s infinite`,
                    willChange: 'transform',
                    '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                  }}
                >
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '10px',
                      backgroundColor: 'rgba(255, 98, 12,0.12)',
                      border: '1px solid rgba(255, 98, 12,0.25)',
                      color: '#ff7d37',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      animation: `${iconGlow} 4s ease-in-out ${i * 1.1}s infinite`,
                      '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        color: '#FFFFFF',
                        fontWeight: 600,
                        fontSize: '0.9375rem',
                        letterSpacing: '-0.01em',
                        mb: 0.25,
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      sx={{
                        color: 'rgba(255,255,255,0.55)',
                        fontSize: '0.8125rem',
                        lineHeight: 1.5,
                        letterSpacing: '-0.01em',
                      }}
                    >
                      {item.desc}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            ))}
          </Stack>
        </Box>

        {/* Rodapé: footer */}
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Typography
            variant="caption"
            sx={{ color: 'rgba(255,255,255,0.3)', letterSpacing: '-0.01em' }}
          >
            Originei © 2025 · Todos os direitos reservados
          </Typography>
        </Box>
      </Box>

      {/* ─────────────────────────────────────────────────────────
         PAINEL DIREITO — Form de login
      ───────────────────────────────────────────────────────── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'center',
          padding: { xs: '32px 20px', sm: 6 },
          backgroundColor: AUTH_PANEL_BG,
          borderLeft: { md: '1px solid rgba(255,255,255,0.06)' },
          position: 'relative',
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 420 }}>
          {/* Logo mobile (no fluxo, acima do form) */}
          <Box
            sx={{
              display: { xs: 'flex', md: 'none' },
              flexDirection: 'column',
              alignItems: 'flex-start',
              mb: 3,
            }}
          >
            <OrigineiLogo size={64} />
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mt: 0.75,
                color: '#9CA3AF',
                fontSize: '0.7rem',
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                fontWeight: 600,
              }}
            >
              Plataforma de Consultores
            </Typography>
          </Box>

          {/* Heading */}
          <Box sx={{ mb: 4 }}>
            <Typography
              sx={{
                fontSize: '1.625rem',
                fontWeight: 700,
                color: '#FFFFFF',
                letterSpacing: '-0.025em',
                mb: 0.75,
              }}
            >
              {step === 'senha'
                ? 'Acesse sua conta'
                : step === 'enroll'
                  ? 'Configure a verificação em duas etapas'
                  : step === 'recovery-codes'
                    ? 'Guarde seus códigos de recuperação'
                    : 'Verificação em duas etapas'}
            </Typography>
            <Typography
              sx={{
                color: 'rgba(255,255,255,0.6)',
                fontSize: '0.9375rem',
                lineHeight: 1.5,
                letterSpacing: '-0.01em',
              }}
            >
              {step === 'senha'
                ? 'Entre com suas credenciais para continuar.'
                : step === 'enroll'
                  ? 'Escaneie o QR Code no app autenticador e informe o primeiro código.'
                  : step === 'recovery-codes'
                    ? 'Use-os para entrar caso perca o acesso ao app autenticador.'
                    : usandoRecovery
                      ? 'Informe um dos seus códigos de recuperação.'
                      : 'Informe o código de 6 dígitos do seu app autenticador.'}
            </Typography>
          </Box>

          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 3,
                borderRadius: '10px',
                fontSize: '0.875rem',
                border: '1px solid rgba(231,76,60,0.2)',
              }}
            >
              {error}
            </Alert>
          )}

          {step === 'senha' && (
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            {/* Email */}
            <Box sx={{ mb: 2 }}>
              <Typography
                component="label"
                htmlFor="email"
                sx={fieldLabelSx}
              >
                E-mail
              </Typography>
              <TextField
                id="email"
                {...register('email')}
                type="email"
                fullWidth
                placeholder="voce@empresa.com.br"
                variant="outlined"
                error={!!errors.email}
                helperText={errors.email?.message}
                autoComplete="email"
                disabled={isLoading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailOutlinedIcon sx={{ fontSize: 20, color: '#9CA3AF' }} />
                    </InputAdornment>
                  ),
                  sx: fieldInputSx,
                }}
              />
            </Box>

            {/* Senha */}
            <Box sx={{ mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                <Typography
                  component="label"
                  htmlFor="senha"
                  sx={{
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                    color: 'rgba(255,255,255,0.92)',
                    letterSpacing: '-0.01em',
                  }}
                >
                  Senha
                </Typography>
                <Link
                  component={RouterLink}
                  to="/forgot-password"
                  underline="hover"
                  sx={{
                    color: '#ff620c',
                    fontSize: '0.8125rem',
                    fontWeight: 500,
                    letterSpacing: '-0.01em',
                    cursor: 'pointer',
                    '&:hover': { color: '#e0560a' },
                  }}
                >
                  Esqueci minha senha
                </Link>
              </Box>
              <TextField
                id="senha"
                {...register('senha')}
                type={showPassword ? 'text' : 'password'}
                fullWidth
                placeholder="••••••••"
                variant="outlined"
                error={!!errors.senha}
                helperText={errors.senha?.message}
                autoComplete="current-password"
                disabled={isLoading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlinedIcon sx={{ fontSize: 20, color: '#9CA3AF' }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        disabled={isLoading}
                        size="small"
                      >
                        {showPassword ? (
                          <VisibilityOffIcon sx={{ fontSize: 18 }} />
                        ) : (
                          <VisibilityIcon sx={{ fontSize: 18 }} />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                  sx: fieldInputSx,
                }}
              />
            </Box>

            {/* Manter conectado */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  size="small"
                  sx={{
                    color: 'rgba(255,255,255,0.4)',
                    '&.Mui-checked': { color: '#ff620c' },
                  }}
                />
              }
              label={
                <Typography sx={{ fontSize: '0.8125rem', color: 'rgba(255,255,255,0.7)', letterSpacing: '-0.01em' }}>
                  Manter conectado neste dispositivo
                </Typography>
              }
              sx={{ mb: 2.5, mt: 0.5 }}
            />

            {/* Botão principal */}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={isLoading}
              endIcon={!isLoading ? <ArrowForwardIcon /> : undefined}
              sx={primaryBtnSx}
            >
              {isLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={18} color="inherit" />
                  Entrando...
                </Box>
              ) : (
                'Entrar com segurança'
              )}
            </Button>

            {/* Entrada do funil (PDF, Etapa 1). Sem este link a página pública de
                candidatura existe mas não é alcançável por ninguém. */}
            <Typography
              sx={{
                fontSize: '0.8125rem',
                color: 'rgba(255,255,255,0.7)',
                textAlign: 'center',
                mt: 2.5,
                letterSpacing: '-0.01em',
              }}
            >
              Ainda não é consultor?{' '}
              <Link
                component={RouterLink}
                to="/quero-ser-consultor"
                underline="hover"
                sx={{ color: '#ff620c', fontWeight: 600, '&:hover': { color: '#e0560a' } }}
              >
                Quero ser um Consultor
              </Link>
            </Typography>

            <Typography
              sx={{
                fontSize: '0.7rem',
                color: '#9CA3AF',
                textAlign: 'center',
                mt: 2,
                letterSpacing: '-0.01em',
                lineHeight: 1.5,
              }}
            >
              Ao entrar você concorda com os nossos{' '}
              <Link
                href="/termos"
                target="_blank"
                rel="noreferrer"
                underline="hover"
                sx={{ color: '#ff620c', fontWeight: 600 }}
              >
                Termos de Uso
              </Link>
              {' '}e a{' '}
              <Link
                href="/privacidade"
                target="_blank"
                rel="noreferrer"
                underline="hover"
                sx={{ color: '#ff620c', fontWeight: 600 }}
              >
                Política de Privacidade
              </Link>
              .
            </Typography>
          </form>
          )}

          {step === 'codigo' && (
          <form onSubmit={onSubmitCodigo} noValidate>
            <Box sx={{ mb: 2 }}>
              <Typography
                component="label"
                htmlFor="totp"
                sx={fieldLabelSx}
              >
                {usandoRecovery ? 'Código de recuperação' : 'Código de verificação'}
              </Typography>
              <TextField
                id="totp"
                value={codigo}
                onChange={(e) =>
                  setCodigo(
                    usandoRecovery
                      ? e.target.value.toUpperCase().slice(0, 11)
                      : e.target.value.replace(/\D/g, '').slice(0, 6),
                  )
                }
                fullWidth
                autoFocus
                placeholder={usandoRecovery ? 'XXXXX-XXXXX' : '000000'}
                variant="outlined"
                disabled={isLoading}
                inputProps={
                  usandoRecovery
                    ? {
                        autoComplete: 'one-time-code',
                        maxLength: 11,
                        style: { letterSpacing: '0.15em', fontSize: '1.1rem', textAlign: 'center' },
                      }
                    : {
                        inputMode: 'numeric',
                        autoComplete: 'one-time-code',
                        maxLength: 6,
                        style: { letterSpacing: '0.5em', fontSize: '1.25rem', textAlign: 'center' },
                      }
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <VerifiedUserOutlinedIcon sx={{ fontSize: 20, color: '#9CA3AF' }} />
                    </InputAdornment>
                  ),
                  sx: fieldInputSx,
                }}
              />
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={isLoading || !codigoValido}
              endIcon={!isLoading ? <ArrowForwardIcon /> : undefined}
              sx={primaryBtnSx}
            >
              {isLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={18} color="inherit" />
                  Verificando...
                </Box>
              ) : (
                'Verificar e entrar'
              )}
            </Button>

            <Stack spacing={1} alignItems="center" sx={{ mt: 2 }}>
              <Link
                component="button"
                type="button"
                onClick={alternarRecovery}
                underline="hover"
                sx={{ color: '#ff620c', fontSize: '0.8125rem', fontWeight: 600, cursor: 'pointer' }}
              >
                {usandoRecovery ? 'Usar o app autenticador' : 'Usar código de recuperação'}
              </Link>
              <Link
                component="button"
                type="button"
                onClick={voltarParaSenha}
                underline="hover"
                sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.8125rem', fontWeight: 500, cursor: 'pointer' }}
              >
                ← Voltar e usar outra conta
              </Link>
            </Stack>
          </form>
          )}

          {step === 'enroll' && enrollData && (
          <form onSubmit={onSubmitEnroll} noValidate>
            {/* QR Code */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Box
                component="img"
                src={enrollData.qrCodeDataUri}
                alt="QR Code de configuração do 2FA"
                sx={{
                  width: 188,
                  height: 188,
                  borderRadius: '12px',
                  border: '1px solid #E5E5E7',
                  padding: '8px',
                  backgroundColor: '#FFFFFF',
                }}
              />
            </Box>

            {/* Secret para entrada manual */}
            <Box
              sx={{
                mb: 2.5,
                px: 1.5,
                py: 1,
                borderRadius: '8px',
                backgroundColor: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.10)',
                textAlign: 'center',
              }}
            >
              <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)', mb: 0.25 }}>
                Não consegue escanear? Use este código:
              </Typography>
              <Typography
                sx={{ fontSize: '0.8125rem', fontWeight: 600, color: 'rgba(255,255,255,0.85)', letterSpacing: '0.06em', wordBreak: 'break-all' }}
              >
                {enrollData.secret}
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography
                component="label"
                htmlFor="totp-enroll"
                sx={fieldLabelSx}
              >
                Código do app autenticador
              </Typography>
              <TextField
                id="totp-enroll"
                value={codigo}
                onChange={(e) => setCodigo(e.target.value.replace(/\D/g, '').slice(0, 6))}
                fullWidth
                autoFocus
                placeholder="000000"
                variant="outlined"
                disabled={isLoading}
                inputProps={{
                  inputMode: 'numeric',
                  autoComplete: 'one-time-code',
                  maxLength: 6,
                  style: { letterSpacing: '0.5em', fontSize: '1.25rem', textAlign: 'center' },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <VerifiedUserOutlinedIcon sx={{ fontSize: 20, color: '#9CA3AF' }} />
                    </InputAdornment>
                  ),
                  sx: fieldInputSx,
                }}
              />
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={isLoading || !codigoValido}
              endIcon={!isLoading ? <ArrowForwardIcon /> : undefined}
              sx={primaryBtnSx}
            >
              {isLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={18} color="inherit" />
                  Ativando...
                </Box>
              ) : (
                'Ativar e entrar'
              )}
            </Button>

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Link
                component="button"
                type="button"
                onClick={voltarParaSenha}
                underline="hover"
                sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.8125rem', fontWeight: 500, cursor: 'pointer' }}
              >
                ← Voltar
              </Link>
            </Box>
          </form>
          )}

          {step === 'recovery-codes' && (
          <Box>
            <RecoveryCodesDisplay codes={recoveryCodes} />
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={() => navigate('/dashboard')}
              endIcon={<ArrowForwardIcon />}
              sx={{ mt: 3, ...primaryBtnSx }}
            >
              Já guardei — continuar
            </Button>
          </Box>
          )}

          {/* Footer info */}
          <Box
            sx={{
              mt: 4,
              pt: 3,
              borderTop: '1px solid rgba(255,255,255,0.08)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Typography sx={{ fontSize: '0.75rem', color: '#9CA3AF', letterSpacing: '-0.01em' }}>
              Não tem acesso?{' '}
              <Link
                href="/contato"
                underline="hover"
                sx={{ color: '#ff620c', fontWeight: 600, cursor: 'pointer' }}
              >
                Fale com seu gestor
              </Link>
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <VerifiedUserOutlinedIcon sx={{ fontSize: 14, color: '#9CA3AF' }} />
              <Typography sx={{ fontSize: '0.7rem', color: '#9CA3AF', letterSpacing: '-0.01em' }}>
                Conexão segura · LGPD
              </Typography>
            </Stack>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
